import { SettingsStore } from '@mobx/services/SettingsStore';
import { Container } from 'inversify';
import { ProfileModule } from '@mobx/services/profile';
import { ProfileModel } from '@mobx/services/profile/model';
import { PartialInvoicesStore } from '@widgets/partial-invoices-table';
import { AuthServiceImpl } from '@mobx/services/auth';
import { RegistrationModule } from '@mobx/services/registration';
import { RegistrationModel } from '@mobx/services/registration/model';

import { configurePersistable } from 'mobx-persist-store';
import { RolesServiceImpl } from '@mobx/services/roles';
import { AccountReportsModel } from '@mobx/services/accountReports/model';
import { AccountReportsModuleImpl } from '@mobx/services/accountReports';
import { AppartmentsModule } from '@mobx/services/appartments';
import { HistoryModule } from '@mobx/services/history';

import { router } from '@app/routes';
import { DictionaryModule } from '@mobx/services/dictionaries/model/DictionaryModule';
import { InstructionsModule } from '@mobx/services/instructions';
import { ContactService } from '@shared/side-home/model/ContactService';
import { AccountReportsModule, AccountReportsModuleIdentifier } from '@shared/types/mobx/services/accountReports';
import { type AuthService, AuthServiceIdentifier } from '@shared/types/mobx/services/auth';
import { type RolesService, RolesServiceIdentifier } from '@shared/types/mobx/services/roles';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import {
  type MonthlyConfirmationService,
  MonthlyConfirmationServiceIdentifier,
  MonthlyConfirmationServiceImpl
} from '@mobx/services/monthlyConfirmation';
import { OsiServiceImpl } from '../services/osi';

configurePersistable(
  { storage: window.localStorage, expireIn: 86400000, removeOnExpiration: true, stringify: true, debugMode: false },
  { fireImmediately: true }
);

const serviceContainer = new Container();

BindAuthService();
BindProfile();
BindSettings();
BindPartialInvoices();
BindRoles();
BindAccountReports();
BindAppartments();
BindRegistration();
BindHistory();
BindOsiService();
BindMonthlyConfirmation();
BindDictionaries();
BindInstructions();
BindContactService();

function BindAuthService() {
  serviceContainer.bind<AuthService>(AuthServiceIdentifier).to(AuthServiceImpl).inSingletonScope();
}

function BindProfile() {
  serviceContainer.bind<ProfileModule>(ProfileModule).toSelf().inSingletonScope();
  serviceContainer.bind<ProfileModel>(ProfileModel).toSelf().inSingletonScope();
}

function BindSettings() {
  serviceContainer.bind<SettingsStore>(SettingsStore).toSelf().inSingletonScope();
}

function BindPartialInvoices() {
  serviceContainer.bind<PartialInvoicesStore>(PartialInvoicesStore).toSelf().inSingletonScope();
}

function BindRegistration() {
  serviceContainer.bind<RegistrationModule>(RegistrationModule).toSelf().inSingletonScope();
  serviceContainer.bind<RegistrationModel>(RegistrationModel).toSelf().inSingletonScope();
}

function BindRoles() {
  serviceContainer.bind<RolesService>(RolesServiceIdentifier).to(RolesServiceImpl).inSingletonScope();
}

function BindAccountReports() {
  serviceContainer
    .bind<AccountReportsModule>(AccountReportsModuleIdentifier)
    .to(AccountReportsModuleImpl)
    .inSingletonScope();
  serviceContainer.bind<AccountReportsModel>(AccountReportsModel).toSelf().inSingletonScope();
}

function BindAppartments() {
  serviceContainer.bind<AppartmentsModule>(AppartmentsModule).toSelf().inSingletonScope();
}

function BindHistory() {
  serviceContainer.bind<HistoryModule>(HistoryModule).toSelf().inSingletonScope();
  serviceContainer.resolve(HistoryModule).setRouter(router);
}

function BindOsiService() {
  serviceContainer.bind<OsiService>(OsiServiceIdentifier).to(OsiServiceImpl).inSingletonScope();
}

function BindMonthlyConfirmation() {
  serviceContainer
    .bind<MonthlyConfirmationService>(MonthlyConfirmationServiceIdentifier)
    .to(MonthlyConfirmationServiceImpl)
    .inSingletonScope();
}

function BindDictionaries() {
  serviceContainer.bind<DictionaryModule>(DictionaryModule).toSelf().inSingletonScope();
}

function BindInstructions() {
  serviceContainer.bind<InstructionsModule>(InstructionsModule).toSelf().inSingletonScope();
}

function BindContactService() {
  serviceContainer.bind<ContactService>(ContactService).toSelf().inSingletonScope();
}

export default serviceContainer;
